import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Funcionario, FuncionarioFormData, ApiResponse } from '../types';

// Gerar código único para funcionário
const generateFuncionarioCode = (): string => {
  const randomNum = Math.floor(Math.random() * 9000) + 1000; // 1000-9999
  return `VEN-${randomNum}`;
};

// Verificar se código já existe
const checkCodeExists = async (codigo: string, userId: string): Promise<boolean> => {
  try {
    const q = query(
      collection(db, 'funcionarios'),
      where('codigo', '==', codigo),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Erro ao verificar código:', error);
    return true; // Em caso de erro, assumir que existe para evitar duplicatas
  }
};

// Gerar código único que não existe
const generateUniqueCode = async (userId: string): Promise<string> => {
  let codigo: string;
  let exists = true;
  
  while (exists) {
    codigo = generateFuncionarioCode();
    exists = await checkCodeExists(codigo, userId);
  }
  
  return codigo!;
};

// Criar funcionário
export const createFuncionario = async (funcionarioData: FuncionarioFormData, userId: string): Promise<ApiResponse<Funcionario>> => {
  try {
    const codigo = await generateUniqueCode(userId);
    
    const funcionarioData_toSave = {
      nome: funcionarioData.nome,
      codigo,
      ativo: true,
      userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'funcionarios'), funcionarioData_toSave);

    const funcionario: Funcionario = {
      id: docRef.id,
      nome: funcionarioData.nome,
      codigo,
      ativo: true,
      userId,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    return {
      success: true,
      data: funcionario,
      id: docRef.id
    };
  } catch (error) {
    console.error('❌ Erro ao criar funcionário:', error);
    return {
      success: false,
      error: 'Erro ao criar funcionário'
    };
  }
};

// Buscar funcionários
export const getFuncionarios = async (userId: string): Promise<ApiResponse<Funcionario[]>> => {
  try {
    if (!userId) {
      return {
        success: false,
        error: 'Usuário não autenticado'
      };
    }

    let q = query(
      collection(db, 'funcionarios'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    try {
      const querySnapshot = await getDocs(q);
      const funcionarios: Funcionario[] = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      } as Funcionario));

      return {
        success: true,
        data: funcionarios
      };
      
    } catch (orderByError) {
      // Se orderBy falhar, tentar query simples sem ordenação
      q = query(
        collection(db, 'funcionarios'),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      const funcionarios: Funcionario[] = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      } as Funcionario));

      // Ordenar no JavaScript (mais recente primeiro)
      funcionarios.sort((a, b) => {
        const dateA = a.createdAt || new Date(0);
        const dateB = b.createdAt || new Date(0);
        return dateB.getTime() - dateA.getTime();
      });

      return {
        success: true,
        data: funcionarios
      };
    }
    
  } catch (error: any) {
    console.error('Erro ao buscar funcionários:', error);
    return {
      success: false,
      error: `Erro ao buscar funcionários: ${error.message}`
    };
  }
};

// Atualizar funcionário
export const updateFuncionario = async (id: string, updates: Partial<Funcionario>): Promise<ApiResponse<void>> => {
  try {
    const funcionarioRef = doc(db, 'funcionarios', id);
    await updateDoc(funcionarioRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });

    return {
      success: true
    };
  } catch (error) {
    console.error('Erro ao atualizar funcionário:', error);
    return {
      success: false,
      error: 'Erro ao atualizar funcionário'
    };
  }
};

// Deletar funcionário
export const deleteFuncionario = async (id: string): Promise<ApiResponse<void>> => {
  try {
    await deleteDoc(doc(db, 'funcionarios', id));
    return {
      success: true
    };
  } catch (error) {
    console.error('Erro ao deletar funcionário:', error);
    return {
      success: false,
      error: 'Erro ao deletar funcionário'
    };
  }
};

// Buscar funcionário por código (busca global para funcionários)
export const getFuncionarioByCode = async (codigo: string, userId?: string): Promise<ApiResponse<Funcionario | null>> => {
  try {
    let q;
    
    if (userId && userId !== 'global') {
      // Buscar apenas funcionários do usuário específico
      q = query(
        collection(db, 'funcionarios'),
        where('codigo', '==', codigo),
        where('userId', '==', userId),
        where('ativo', '==', true)
      );
    } else {
      // Buscar em todos os funcionários (para login de funcionário)
      q = query(
        collection(db, 'funcionarios'),
        where('codigo', '==', codigo),
        where('ativo', '==', true)
      );
    }
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return {
        success: true,
        data: null
      };
    }

    const doc = querySnapshot.docs[0];
    const funcionario: Funcionario = {
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    } as Funcionario;

    return {
      success: true,
      data: funcionario
    };
  } catch (error) {
    console.error('❌ Erro ao buscar funcionário por código:', error);
    console.error('❌ Detalhes do erro:', error);
    return {
      success: false,
      error: 'Erro ao buscar funcionário'
    };
  }
};
